import {
  BOX_PLOT_LABEL_Z_INDEX,
  DEFAULT_BOX_PLOT_Z_INDEX,
  JITTER_PLOT_LEGEND_ITEMS_SERIES_NAME,
  SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import {
  BoxPlotDisplayMode,
  resolveBoxPlotDisplayMode
} from "entities/charts/boxPlotDisplayMode";
import { createEntityState } from "entities/charts/factory";
import {
  ChartOption,
  DEFAULT_CHART_OPTIONS,
  UNDEFINED_LOCK_OPTION
} from "entities/charts/options";
import { DataRange } from "types";
import { SeriesType } from "types/echarts";
import { EvaChartStates } from "types/factory";

import { calculateBoxPlotBarWidth } from "components/charts/utils/calculateBoxPlotBarWidth";
import { getJitterPlotPosition } from "components/charts/utils/getJitterPlotPosition";
import { getViolinPlotPosition } from "components/charts/utils/getViolinPlotPosition";

import { createChartLocks } from "../locks";
import { prepareBoxPlotData, sortBoxSeriesByMedian } from "./boxPlotDataProcessing";
import { pSBC } from "./pSBC";
import { updateOptionsWithTypeWellsSeries } from "./updateOptionsWithTypeWellSeries";
import { formatChartTitleWithSubscript, getLineSeriesMinMax } from "./utils";
import {
  DATA_RANGE_LABELS,
  buildViolinGroup,
  filterValuesWithinBounds,
  trimBounds
} from "./violinDataProcessing";

const X_AXIS_SLIDER_ID = "xAxis-slider";
const HORIZONTAL_ZOOM_ORIENT = "horizontal";
const VIOLIN_FILL_OPACITY = 0.6;

// Strip the "(count) " prefix a box plot label/group carries, e.g. "(12) Permian" -> "Permian".
// Split on the first ")" only so quantile suffixes survive, e.g. "(1) 3.1 - 4.2 (Q1)" ->
// "3.1 - 4.2 (Q1)". Null-safe: a missing or unparseable label yields "" instead of throwing, so
// the violin's api.coord lookup, the jitter grouping, and the x-axis category derivation all agree
// on the same string even when the whitespace after the count is irregular.
export function categoryFromLabel(label?: string | null): string {
  return label?.split(/\)(.*)/s)?.[1]?.trim() ?? "";
}

interface DataZoomEntry {
  id?: string;
  type?: string;
  orient?: string;
  filterMode?: string;
  [key: string]: unknown;
}

// filterMode "none" causes ECharts 5.x to collapse all boxes into one when the
// viewport is zoomed to a single category. Use "filter" so out-of-view categories
// are removed rather than rendered into a zero-width band.
// NOTE: The condition keys off DEFAULT_CHART_OPTIONS.dataZoom entry shapes
// (X_AXIS_SLIDER_ID, HORIZONTAL_ZOOM_ORIENT). If that array is restructured,
// update the constants above — the tests in boxPlot.test.ts will catch a silent no-op.
export function getBoxPlotDataZoom(): DataZoomEntry[] {
  return (DEFAULT_CHART_OPTIONS.dataZoom as DataZoomEntry[]).map((dz) =>
    dz.id === X_AXIS_SLIDER_ID || dz.orient === HORIZONTAL_ZOOM_ORIENT
      ? { ...dz, filterMode: "filter" }
      : dz
  );
}

function setJitter(
  option,
  traces,
  pointSize,
  pointOpacity,
  overrideSeries = true,
  globalTrimBounds: { lo: number; hi: number } | null = null
) {
  const groups: {
    [name: string]: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      style: any;
      data: (string | number | unknown)[];
    };
  } = {};

  if (!option.uwiCoordinates) {
    return;
  }

  Object.keys(option.uwiCoordinates).forEach((key) => {
    const point = option.uwiCoordinates[key];
    const groupName = categoryFromLabel(point?.group);
    // Data Range: hide points outside the chart-wide percentile bounds so the visible
    // points always describe the same wells as the trimmed box stats. Bounds are null
    // when nothing is trimmed (range "all", or no finite values in the response) —
    // then every point shows.
    if (
      globalTrimBounds &&
      (point.y < globalTrimBounds.lo || point.y > globalTrimBounds.hi)
    ) {
      return;
    }
    // The scatter series that this jitter plot belongs to
    const scatterSeries = traces.find(
      (t) => t.type === "scatter" && t.uwiIndices.includes(point.index)
    );

    if (!(groupName in groups)) {
      groups[groupName] = { ...option.uwiCoordinates[key], data: [] };
    }

    groups[groupName].data.push([
      groupName,
      point.y,
      point.style?.hexColor, // point color
      key,
      scatterSeries?.isForecast,
      pointOpacity,
      point.style?.hexColor, // original color is required for tooltip
      0 // emphasis flag (0 | 1), set by jitterPlotHighlightedSeries
    ]);
  });

  if (overrideSeries && Object.keys(groups).length > 0) {
    option.series = [
      ...(Array.isArray(option.series) ? option.series : [option.series]),
      ...Object.keys(groups).map((key) => {
        const point = groups[key];
        return {
          type: SeriesType.Custom,
          customType: "jitter",
          zlevel: 0,
          id: `(${groups[key].data.length}) ${key}`,
          name: formatChartTitleWithSubscript(`(${groups[key].data.length}) ${key}`),
          data: point.data,
          large: false,
          progressive: 0,
          // styles are not used, individual styles are from the renderItem fn.
          // left here to avoid undefined errors
          line: {
            originalWidth: point.style?.thickness,
            originalColor: point.style?.hexColor,
            originalBorderColor: "none"
          },
          lineStyle: {
            color: point.style?.hexColor,
            width: 1,
            type: "solid"
          },
          itemStyle: {
            color: point.style?.hexColor,
            borderColor: point.style?.hexColor,
            borderWidth: 1,
            opacity: pointOpacity
          },
          renderItem: (_params, api) => {
            return getJitterPlotPosition(api, pointSize / 2);
          }
        };
      })
    ];
  }
}

function setViolin(option, traces, overrideSeries = true) {
  if (!overrideSeries) {
    return;
  }

  const violinSeries = traces
    .filter((t) => t.type === "box")
    .map((trace) => {
      const { label, style, y } = trace;
      const categoryValue = categoryFromLabel(label);
      const group = buildViolinGroup(y ?? [], { name: categoryValue });
      if (group.n === 0) return null;

      const color = style?.hexColor;
      const renderData = {
        curve: group.curve,
        groupMaxDensity: group.groupMaxDensity,
        degenerate: group.degenerate,
        color,
        opacity: VIOLIN_FILL_OPACITY
      };

      return {
        type: SeriesType.Custom,
        customType: "violin",
        zlevel: 0,
        clip: true,
        silent: true,
        id: `violin-${label}`,
        name: label,
        data: [{ value: [categoryValue] }],
        large: false,
        progressive: 0,
        // Not read by ECharts; survives getOption() so window.__evaQA.violins can read the curve.
        violinGroup: group,
        renderItem: (_params, api) => getViolinPlotPosition(api, renderData)
      };
    })
    .filter(Boolean);

  option.series = [
    ...(Array.isArray(option.series) ? option.series : [option.series]),
    ...violinSeries
  ];
}

export function translateBoxPlotResponseToOptions(states: Partial<EvaChartStates>) {
  const {
    title,
    layout,
    series: rawSeries,
    typeWellSeries = [],
    uwiCoordinates,
    overrideSeries = true,
    bounds,
    chartType,
    jitterPlotToggle,
    violinPlotToggle,
    dataRangeSelect,
    xAxisSort,
    getPointStyle
  } = states;
  const displayMode = resolveBoxPlotDisplayMode({
    jitterActive: jitterPlotToggle?.active,
    violinActive: violinPlotToggle?.active
  });
  const violinEnabled = displayMode === BoxPlotDisplayMode.Violin;
  const jitterEnabled = displayMode === BoxPlotDisplayMode.Jitter;
  const trimRange = (dataRangeSelect?.properties?.range ?? "all") as DataRange;
  const dataRangeLabel = trimRange === "all" ? undefined : DATA_RANGE_LABELS[trimRange];
  // The Data Range setting trims every display mode of the box plot family: the plain
  // box stats, the box under the jitter, and the violin's inner box + silhouette all
  // read the same trimmed values, and setJitter hides the per-well points outside the
  // bounds. Modeled on the Insights engine (ChartController.java): the percentile
  // bounds are computed ONCE from the whole chart's values (all groups pooled), then
  // every group keeps only its values inside those global bounds. Per-group bounds
  // would empty small groups — a 2-well group's own P1/P99 land strictly between its
  // two values — while global bounds only drop the population's true extremes.
  const globalTrimBounds = trimBounds(
    rawSeries.filter((s) => s.type === "box").flatMap((s) => s.y ?? []),
    trimRange
  );
  // Scatter series are trimmed too: their y values feed the axis range
  // (getLineSeriesMinMax), so leaving them raw would hold the axis open around
  // points the trim hides. The rendered jitter points come from uwiCoordinates,
  // filtered separately in setJitter with the same bounds.
  const trimmedSeries = globalTrimBounds
    ? rawSeries.map((s) =>
        s.type === "box" || s.type === "scatter"
          ? { ...s, y: filterValuesWithinBounds(s.y ?? [], globalTrimBounds) }
          : s
      )
    : rawSeries;
  // Sorted after the trim so the median ranking matches the box that is drawn.
  const series = sortBoxSeriesByMedian(trimmedSeries, xAxisSort?.properties?.value);
  const yAxisTitle = layout?.yAxis?.title || "";
  const { yMin, yMax } = getLineSeriesMinMax(series);
  const { lockedYMax, lockedYMin } = createChartLocks({
    type: EvaChart.BoxPlot,
    states
  });
  const width = bounds?.width;
  const xCategories = new Set(
    series
      .filter((s) => s.label !== JITTER_PLOT_LEGEND_ITEMS_SERIES_NAME)
      .map((s) => s.label)
  );

  // Conditionally include the "series" property only if overrideSeries is true.
  const baseOptions = {
    chartType,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title),
        // Tells the viewer the box stats leave out the values outside the trim bounds.
        ...(dataRangeLabel
          ? { subtext: `Data Range: ${dataRangeLabel}`, itemGap: 8 }
          : {}),
        z: BOX_PLOT_LABEL_Z_INDEX
      })
    },
    yAxis: {
      ...createEntityState(ChartOption.YAxis, {
        name: formatChartTitleWithSubscript(yAxisTitle),
        nameGap: 43,
        nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
        min: lockedYMin,
        max: lockedYMax,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock:
          Number.isFinite(yMin.value) && Number.isFinite(yMax.value)
            ? {
                show: true, // it manages if lock should be shown or not
                min: {
                  value: Math.floor(yMin.value),
                  type: yMin.type
                },
                max: {
                  value: Math.floor(yMax.value),
                  type: yMax.type,
                  override: true // it manages if lock should be overridden or not
                }
              }
            : UNDEFINED_LOCK_OPTION,
        axisPointer: {
          label: {
            margin: SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN
          }
        }
      })
    },
    xAxis: {
      ...createEntityState(ChartOption.XAxisCategory, {
        name: formatChartTitleWithSubscript(layout?.xAxis?.title || ""),
        data: series
          .filter(
            (t) => t.type === "box" && t.label !== JITTER_PLOT_LEGEND_ITEMS_SERIES_NAME
          )
          .map((s) => categoryFromLabel(s.label)),
        nameGap: 27,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        useY: false, // useY is managed at hd chart
        axisLabel: {
          width: calculateBoxPlotBarWidth(width, xCategories?.size),
          padding: [5, 2, 0, 0],
          overflow: "truncate",
          interval: 0
        },
        silent: true,
        z: BOX_PLOT_LABEL_Z_INDEX
      })
    },
    uwiCoordinates: uwiCoordinates,
    grid: { top: 46, bottom: 46, left: 62, right: 20 },
    dataZoom: getBoxPlotDataZoom(),
    // The series property is conditionally added below
    ...(overrideSeries && {
      series: createEntityState(ChartOption.Box, {
        name: "box",
        id: "box",
        data: series
          .filter((x) => x.label !== JITTER_PLOT_LEGEND_ITEMS_SERIES_NAME)
          .map((location) => {
            const { style, y, type, label } = location;
            if (type !== "box") return;
            if (!y) return;
            const data = prepareBoxPlotData([y], { layout: "vertical" });
            const formattedData = data.boxData.map((boxData, index) => ({
              // check for placeholders
              value: y.length ? boxData : [],
              name: label,
              outliers: data.outliers[index],
              zlevel: DEFAULT_BOX_PLOT_Z_INDEX,
              itemStyle: {
                // Violin mode fills the box at 40% alpha (8-digit hex) so the
                // silhouette underneath stays readable, matching the Insights
                // look; the border stays solid via opacity 1 on the item.
                color:
                  violinEnabled && style?.hexColor
                    ? `${style.hexColor}66`
                    : jitterEnabled && !!uwiCoordinates
                    ? "none"
                    : style?.hexColor,
                opacity: 1,
                borderColor: pSBC(-0.42, style?.hexColor),
                borderWidth: 1
              }
            }));
            return formattedData;
          })
          .filter(Boolean)
          .flat()
      })
    })
  };

  let options = createEntityState(ChartOption.Chart, baseOptions);

  if (series.length === 0 && typeWellSeries.length === 0) {
    options = {};
    return options;
  }

  if (violinEnabled) {
    setViolin(
      options,
      series.filter((s) => s.label !== JITTER_PLOT_LEGEND_ITEMS_SERIES_NAME),
      overrideSeries
    );
  } else if (jitterEnabled) {
    const { opacity, size } = getPointStyle();
    setJitter(
      options,
      series.filter((s) => s.label !== JITTER_PLOT_LEGEND_ITEMS_SERIES_NAME),
      size,
      opacity,
      overrideSeries,
      globalTrimBounds
    );
  }

  // Ensure series is an array for type well series to be added
  // (setJitter converts it to an array in jitter mode, but non-jitter mode needs this)
  if (!Array.isArray(options.series) && options.series != null) {
    options.series = [options.series];
  }

  if (typeWellSeries.length > 0) {
    options = updateOptionsWithTypeWellsSeries(options, typeWellSeries);
  }

  return options;
}
